import React, { useState, useEffect, memo } from "react";
import {
  Box,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Chip,
  IconButton,
  Stack,
  CircularProgress,
  Typography,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import RotateLeftIcon from "@mui/icons-material/RotateLeft";
import configServ from "../../services/config";
import toast from "react-hot-toast";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: { style: { maxHeight: ITEM_HEIGHT * 6 + ITEM_PADDING_TOP } },
};

const initialState = { supervisor_id: "", subordinate_ids: [] };

function EmployeeHierarchyCard({ company_id, setIsCreate, setIsChanged }) {
  const [form, setForm]             = useState(initialState);
  const [errors, setErrors]         = useState({});
  const [loading, setLoading]       = useState(false);
  // getSupervisors now returns all active enrolled employees with cm.id as `id`
  // — same list is used for both supervisor and subordinate dropdowns
  const [employees, setEmployees] = useState([]);
  const [fetchingDropdowns, setFetchingDropdowns] = useState(true);

  useEffect(() => {
    const load = async () => {
      setFetchingDropdowns(true);
      try {
        const res = await configServ.getSupervisors({ company_id });
        if (res?.status) setEmployees(res.data || []);
      } catch (e) {
        console.error(e);
      } finally {
        setFetchingDropdowns(false);
      }
    };
    load();
  }, [company_id]);

  const handleSupervisorChange = (e) => {
    setForm((prev) => ({ ...prev, supervisor_id: e.target.value }));
    if (errors.supervisor_id) setErrors((prev) => ({ ...prev, supervisor_id: "" }));
  };

  const handleSubordinatesChange = (e) => {
    const value = typeof e.target.value === "string"
      ? e.target.value.split(",")
      : e.target.value;
    setForm((prev) => ({ ...prev, subordinate_ids: value }));
    if (errors.subordinate_ids) setErrors((prev) => ({ ...prev, subordinate_ids: "" }));
  };

  const handleReset = () => {
    setForm(initialState);
    setErrors({});
  };

  const validate = () => {
    const err = {};
    if (!form.supervisor_id)          err.supervisor_id   = "Supervisor is required";
    if (!form.subordinate_ids.length) err.subordinate_ids = "Select at least one subordinate";
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      const res = await configServ.assignEmployeeHierarchy({
        company_id,
        supervisor_id:  form.supervisor_id,
        subordinate_ids: form.subordinate_ids,
      });

      if (res?.status) {
        const inserted = res.inserted ?? 0;
        const skipped  = res.skipped  ?? 0;
        toast.success(`Assigned: ${inserted} new, ${skipped} already existed`);
        handleReset();
        setIsCreate(false);
        setIsChanged((p) => !p);
      } else {
        toast.error(res?.message || "Operation failed");
      }
    } catch (e) {
      toast.error("Something went wrong");
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const nameOf = (emp) =>
    emp.contact_name || `${emp.fname || ""} ${emp.lname || ""}`.trim() || emp.mobile;

  if (fetchingDropdowns) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: "#e3f3fc", p: 2, borderRadius: 2, m: 1 }}>
      <Grid container spacing={2}>

        {/* Supervisor */}
        <Grid item xs={12} sm={6} md={4}>
          <FormControl fullWidth size="small" error={!!errors.supervisor_id}>
            <InputLabel>Supervisor *</InputLabel>
            <Select
              value={form.supervisor_id}
              label="Supervisor *"
              onChange={handleSupervisorChange}
              sx={{ backgroundColor: "white" }}
            >
              {employees.length === 0 && (
                <MenuItem disabled value="">No employees found</MenuItem>
              )}
              {employees.map((s) => (
                <MenuItem key={s.id} value={s.id}>{nameOf(s)}</MenuItem>
              ))}
            </Select>
            {errors.supervisor_id && (
              <Typography variant="caption" color="error" sx={{ ml: 1 }}>
                {errors.supervisor_id}
              </Typography>
            )}
          </FormControl>
        </Grid>

        {/* Subordinates multi-select */}
        <Grid item xs={12} sm={6} md={6}>
          <FormControl fullWidth size="small" error={!!errors.subordinate_ids}>
            <InputLabel>Subordinates *</InputLabel>
            <Select
              multiple
              value={form.subordinate_ids}
              onChange={handleSubordinatesChange}
              input={<OutlinedInput label="Subordinates *" />}
              renderValue={(selected) => (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {selected.map((id) => {
                    const emp = employees.find((e) => e.id === id);
                    return (
                      <Chip
                        key={id}
                        label={emp ? nameOf(emp) : id}
                        size="small"
                      />
                    );
                  })}
                </Box>
              )}
              MenuProps={MenuProps}
              sx={{ backgroundColor: "white" }}
            >
              {employees
                .filter((e) => e.id !== form.supervisor_id)
                .map((e) => (
                  <MenuItem key={e.id} value={e.id}>{nameOf(e)}</MenuItem>
                ))}
            </Select>
            {errors.subordinate_ids && (
              <Typography variant="caption" color="error" sx={{ ml: 1 }}>
                {errors.subordinate_ids}
              </Typography>
            )}
          </FormControl>
        </Grid>

        {/* Actions */}
        <Grid item xs={12} textAlign="right">
          <Stack direction="row" justifyContent="flex-end" spacing={1}>
            <IconButton
              onClick={handleReset}
              sx={{ backgroundColor: "secondary.main", color: "white", "&:hover": { backgroundColor: "secondary.dark" } }}
              title="Reset"
            >
              <RotateLeftIcon />
            </IconButton>
            <IconButton
              onClick={handleSubmit}
              disabled={loading}
              sx={{ backgroundColor: "primary.main", color: "white", "&:hover": { backgroundColor: "primary.dark" } }}
              title="Assign"
            >
              <SaveIcon />
            </IconButton>
          </Stack>
        </Grid>

      </Grid>
    </Box>
  );
}

export default memo(EmployeeHierarchyCard);
